import { createImageElementFromBlob } from '@/shared/lib/image'
import { readCanvasAsBlob } from '@/shared/lib/reader'

export async function convertImage(
    image: Blob,
    opts: { size: number; format: string; circle?: boolean }
): Promise<Blob> {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) {
        canvas.remove()
        throw new Error('Canvas not supported')
    }

    const img = await createImageElementFromBlob(image).catch((err) => {
        canvas.remove()
        throw err
    })

    // Set canvas size
    canvas.width = opts.size
    canvas.height = opts.size

    // Determine the scaling ratio for the image
    const scale = Math.max(opts.size / img.width, opts.size / img.height)

    // Calculate the position to draw the image at to center it
    const x = (opts.size - img.width * scale) / 2
    const y = (opts.size - img.height * scale) / 2

    // Disable image smoothing to preserve pixelated look
    ctx.imageSmoothingEnabled = false

    // Draw the image and resize it to the canvas size, while preserving aspect ratio
    ctx.drawImage(img, x, y, img.width * scale, img.height * scale)

    if (opts.circle) {
        // Round image into a circle
        ctx.globalCompositeOperation = 'destination-in'
        ctx.beginPath()
        ctx.arc(opts.size / 2, opts.size / 2, opts.size / 2, 0, Math.PI * 2)
        ctx.fill()
    }

    // Read the canvas content as a blob in the specified format
    return readCanvasAsBlob(canvas, opts.format).finally(() => {
        canvas.remove()
        img.remove()
    })
}

/**
 * Resizes a canvas to the specified dimensions
 * Useful for resizing already-edited images (e.g., from react-avatar-editor)
 * @param sourceCanvas - The canvas to resize
 * @param size - The target size (width and height will be equal)
 * @returns A new canvas with the resized image
 */
export function resizeCanvas(sourceCanvas: HTMLCanvasElement, size: number): HTMLCanvasElement {
    const resizedCanvas = document.createElement('canvas')
    resizedCanvas.width = size
    resizedCanvas.height = size

    const ctx = resizedCanvas.getContext('2d')
    if (!ctx) {
        resizedCanvas.remove()
        throw new Error('Canvas not supported')
    }

    ctx.imageSmoothingEnabled = false
    ctx.drawImage(sourceCanvas, 0, 0, size, size)

    return resizedCanvas
}
